/**
 * HandoffCardShell — the surface a message handed back from ANOTHER session
 * renders on.
 *
 * Two cards use it: the specialist hand-off summary a human return produces
 * (InboundSummaryCard), and the two messages an agent-to-agent delegation
 * leaves behind (DelegationMessageCard). Both say the same thing structurally —
 * "this came from another session, here is who, here is where" — so they are
 * one surface, not two that happen to look alike.
 *
 * Extracted after the second card was written by copying the first: the copy
 * reproduced the wrapper, surface and chip class strings byte for byte, and
 * dropped the link's 44px tap target and its visible focus ring on the way. A
 * shared surface is what stops the next copy losing something else.
 *
 * Design (DESIGN.md — NHS substance, Apple craft): a recessed neutral surface
 * with a 2px Action Blue left rail (the single accent, signalling "from another
 * agent"), flat, 1px hairline, `rounded-lg`, no drop shadow. One small neutral
 * chip, deliberately NOT blue, so it never competes with the single blue action.
 */

import { type ReactElement, type ReactNode } from 'react'
import { type LucideIcon, ArrowUpRight } from 'lucide-react'

import { cn } from '@/lib/utils'

interface HandoffCardShellProps {
  /** `data-render` value, so a test can tell the two cards apart. */
  render: string
  /** Extra data attributes for the wrapper (e.g. the delegation kind). */
  dataAttributes?: Record<string, string>
  /** Chip glyph — which direction this message travelled. */
  icon: LucideIcon
  /** Chip text: who this came from, already localised. */
  label: string
  isLatest?: boolean
  /** The message body, plus anything that belongs under it (citations). */
  children: ReactNode
  /** The single affordance, when there is somewhere to go. */
  action?: { label: string; onClick: () => void }
}

export function HandoffCardShell({
  render,
  dataAttributes,
  icon: Icon,
  label,
  isLatest,
  children,
  action,
}: HandoffCardShellProps): ReactElement {
  return (
    <div
      data-testid="message-bubble"
      data-role="assistant"
      data-render={render}
      {...dataAttributes}
      className={cn('flex flex-col items-start', isLatest && 'animate-message-appear')}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-lg border border-neutral-200 bg-neutral-50',
          'border-l-2 border-l-primary pl-4 pr-4 py-3',
        )}
      >
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-neutral-200/70 px-2.5 py-1 text-xs font-semibold text-neutral-700">
          <Icon className="h-3 w-3" aria-hidden="true" />
          <span className="truncate max-w-[240px]">{label}</span>
        </div>

        {children}

        {/* Single affordance — Action Blue text link, scale-95 press, a real
            44px target and a visible keyboard focus ring. */}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className={cn(
              'mt-3 inline-flex items-center gap-1 min-h-[44px] -my-2 py-2',
              'text-md font-semibold text-primary',
              'transition-transform active:scale-95 motion-reduce:transition-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 rounded-sm',
            )}
          >
            {action.label}
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
